import React, { useEffect, useRef } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCellProps,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { MdDelete } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AlertBox from "../../components/common/AlertBox";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { checkAcessRights } from "../../_helper/helper";
import {
  deleteHealthHistory,
  getAllHealthHistories,
} from "./services/healthHistory.services";
import {
  clearHealthHistoryDetails,
  setHealthHistoryID,
} from "./healthHistorySlice";

const HealthHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);
  const LeadID = location?.state?.healthhistory?.LeadID;

  useEffect(() => {
    dispatch(getAllHealthHistories(LeadID));
    return () => {
      dispatch(closeDialog());
    };
  }, []);

  const handleHealthHistoryCreate = () => {
    navigate("/healthhistory/create", {
      state: {
        LeadID: LeadID,
      },
    });
  };
  return (
    <>
      <DeleteHealthHistoryDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Health History List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleHealthHistoryCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              Create Health History
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <HealthHistoryGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const DeleteHealthHistoryDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const LeadID = location?.state?.healthhistory?.LeadID;
  const HealthHistoryID = useAppSelector(
    (state) => state.healthHistory.HealthHistoryID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = await dispatch(deleteHealthHistory(ID));
      if (response.payload?.Data.Status === 200) {
        dispatch(getAllHealthHistories(LeadID));
        dispatch(closeDialog());
        dispatch(clearHealthHistoryDetails());
      } else {
        dispatch(getAllHealthHistories(LeadID));
        dispatch(closeDialog());
        dispatch(clearHealthHistoryDetails());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteHealthHistory" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Health History?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Health History?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(HealthHistoryID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const HealthHistoryGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const HealthHistoryList = useAppSelector(
    (state) => state.healthHistory.HealthHistoryList
  );
  const loading = useAppSelector((state) => state.healthHistory.loading);
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const LeadID = location?.state?.healthhistory?.LeadID;
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? HealthHistoryList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEditItem = (ID: number, LeadID: number) => {
      navigate("/healthhistory/edit", {
        state: { ID, LeadID },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHealthHistory"));
      dispatch(setHealthHistoryID(ID));
    };
    return (
      <td
        className={`action-td ${props?.className}`}
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEditItem(props.dataItem.ID, LeadID)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            themeColor="error"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 6 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {HealthHistoryList && HealthHistoryList?.length > 0 ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(HealthHistoryList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : HealthHistoryList?.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={HealthHistoryList?.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="LeadName"
            title="Client Name"
            width={250}
            cell={(props: any) => (
              <td>
                {isEditAccess ? (
                  <div
                    className="hoverableName"
                    onClick={() =>
                      navigate("/healthhistory/edit", {
                        state: {
                          ID: props.dataItem?.ID,
                        },
                      })
                    }
                  >
                    {props.dataItem?.LeadName}
                  </div>
                ) : (
                  props.dataItem?.LeadName
                )}
              </td>
            )}
          />
          <Column field="Date" title="Date" width={150} />
          <Column field="Name" title="Name" width={250} />
          <Column field="MobileNo" title="Mobile No." width={200} />
          <Column field="Height" title="Height" width={150} />
          <Column field="Weight" title="Weight" width={150} />
          {(isDeleteAccess || isEditAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

export default HealthHistory;
